import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Stack,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Badge,
  Menu,
  MenuItem,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import bubblingCauldron from '../assets/bubblingCauldron.png';
import { useAchievements } from '../hooks/useAchievements';
import { groupAchievementsForPeriod } from '../features/achievements/achievementHelpers';

// Purple dot styling for the achievements indicator
const ACHIEVEMENT_DOT_SX = {
  '& .MuiBadge-dot': {
    backgroundColor: '#ce93d8',
    boxShadow: '0 0 4px 1px rgba(206,147,216,0.5)',
    minWidth: 8,
    height: 8,
  },
};

const teamNavItems = [
  { label: '🦁 Griffins', path: '/teams/griffins' },
  { label: '🐍 Snakes',   path: '/teams/snakes'   },
  { label: '🦡 Badgers',  path: '/teams/badgers'  },
  { label: '🦅 Eagles',   path: '/teams/eagles'   },
];

const questionNavItems = [
  { label: 'Question Breakdown', path: '/question-breakdown' },
  { label: 'Question Difficulty', path: '/question-difficulty' },
];

export default function Header({ user, onLogout, onShowLogin }) {
  const location = useLocation();
  const theme = useTheme();
  const isMobileQuery = useMediaQuery(theme.breakpoints.down('md'));
  const [isHydrated, setIsHydrated] = useState(false);
  const isMobile = isHydrated ? isMobileQuery : false;
  const isSnapshotting = typeof navigator !== 'undefined' && navigator.userAgent.includes('ReactSnap');
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Dropdown anchor state for each desktop menu
  const [teamsAnchor, setTeamsAnchor] = useState(null);
  const [questionsAnchor, setQuestionsAnchor] = useState(null);
  const [moreAnchor, setMoreAnchor] = useState(null);

  // Fetch achievements to drive the profile dot indicator.
  // Hook is enabled only when a userId is present; returns empty when logged out.
  const { data: achievements } = useAchievements(user?.userId ?? '');
  const hasAchievements = groupAchievementsForPeriod(achievements ?? [], 'alltime').length > 0;

  // Helper to check if current route matches
  const isActive = (path) => location.pathname === path;

  // Returns true if any child path in an array is currently active
  const isGroupActive = (items) => items.some((item) => isActive(item.path));

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleNavClick = () => {
    setDrawerOpen(false);
  };

  // Handlers for desktop dropdown menus
  const handleTeamsOpen = (e) => setTeamsAnchor(e.currentTarget);
  const handleTeamsClose = () => setTeamsAnchor(null);

  const handleQuestionsOpen = (e) => setQuestionsAnchor(e.currentTarget);
  const handleQuestionsClose = () => setQuestionsAnchor(null);

  const handleMoreOpen = (e) => setMoreAnchor(e.currentTarget);
  const handleMoreClose = () => setMoreAnchor(null);

  // Top-level nav items (non-dropdown)
  const navItems = [
    { label: 'Play', path: '/quiz' },
    { label: 'Leaderboard', path: '/leaderboard' },
    ...(user?.userRole?.toLowerCase() === 'admin' ? [{ label: 'Admin', path: '/admin' }] : []),
  ];

  // Prevents hydration mismatch: assume desktop layout until client
  // takes over, then switch to the real isMobile value.
  useEffect(() => {
    if (!isSnapshotting) {
      setIsHydrated(true);
    }
  }, []);

  // Profile nav item (used in mobile drawer main list + desktop More menu)
  const profileNavItem = { label: 'Profile', path: '/profile' };

  // Mobile Drawer Content
  const drawer = (
    <Box
      sx={{ width: 250 }}
      role="presentation"
    >
      {/* Drawer Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Menu
        </Typography>
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      {/* User Info */}
      {user && (
        <>
          <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
            <Typography variant="body2" color="text.secondary">
              Logged in as
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {user.username}
            </Typography>
            {hasAchievements && (
              <Typography
                component={Link}
                to="/profile#achievements"
                onClick={handleNavClick}
                sx={{
                  display: 'inline-block',
                  mt: 0.75,
                  fontSize: 13,
                  color: '#ce93d8',
                  textDecoration: 'none',
                  '&:hover': { color: '#e1bee7', textDecoration: 'underline' },
                }}
              >
                View Achievements →
              </Typography>
            )}
          </Box>
          <Divider />
        </>
      )}

      {/* Navigation Links */}
      <List>
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              selected={isActive(item.path)}
              onClick={handleNavClick}
            >
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
        {/* Profile in mobile main list */}
        <ListItem key={profileNavItem.path} disablePadding>
          <ListItemButton
            component={Link}
            to={profileNavItem.path}
            selected={isActive(profileNavItem.path)}
            onClick={handleNavClick}
          >
            <Badge
              variant="dot"
              invisible={!hasAchievements}
              sx={{
                ...ACHIEVEMENT_DOT_SX,
                width: '100%',
                '& .MuiBadge-badge': { right: -6, top: 10 },
              }}
            >
              <ListItemText primary={profileNavItem.label} />
            </Badge>
          </ListItemButton>
        </ListItem>
      </List>

      <Divider />

      {/* Questions Section */}
      <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Questions
        </Typography>
      </Box>
      <List dense>
        {questionNavItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              selected={isActive(item.path)}
              onClick={handleNavClick}
            >
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider />

      {/* Team Pages Section */}
      <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Team Pages
        </Typography>
      </Box>
      <List dense>
        {teamNavItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              selected={isActive(item.path)}
              onClick={handleNavClick}
            >
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider />

      {/* Auth Actions */}
      <Box sx={{ p: 2 }}>
        {user ? (
          <Button
            fullWidth
            variant="contained"
            color="error"
            onClick={() => {
              onLogout();
              handleDrawerToggle();
            }}
          >
            Logout
          </Button>
        ) : (
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={() => {
              onShowLogin();
              handleDrawerToggle();
            }}
          >
            Login / Register
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar position="static" color="transparent" elevation={0}>
        <Toolbar>
          {/* App Title with Cauldron */}
          <Box
            component={Link}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexGrow: 1,
              color: 'text.primary',
              fontWeight: 'bold',
              textDecoration: 'none',
            }}
          >
            <Typography variant="h6" component="span">
              The Quizzler
            </Typography>
            <Box
              component="img"
              src={bubblingCauldron}
              alt="Cauldron"
              sx={{
                maxHeight: { xs: 32, md: 48 },
                width: 'auto',
                ml: 2,
              }}
            />
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <>
              <Stack direction="row" spacing={2} alignItems="center">
                {/* Flat nav items: Play, Leaderboard, (Admin) */}
                {navItems.map((item) => {
                  const active = item.path === '/quiz'
                    ? location.pathname === '/quiz' || location.pathname === '/'
                    : isActive(item.path);

                  return (
                    <Button
                      key={item.path}
                      component={Link}
                      to={item.path}
                      variant={active ? 'contained' : 'outlined'}
                      color={active ? 'primary' : 'inherit'}
                      sx={active ? { bgcolor: 'action.selected' } : {}}
                    >
                      {item.label}
                    </Button>
                  );
                })}

                {/* Teams dropdown */}
                <Button
                  variant={isGroupActive(teamNavItems) ? 'contained' : 'outlined'}
                  color={isGroupActive(teamNavItems) ? 'primary' : 'inherit'}
                  sx={isGroupActive(teamNavItems) ? { bgcolor: 'action.selected' } : {}}
                  endIcon={<KeyboardArrowDownIcon />}
                  onClick={handleTeamsOpen}
                >
                  Teams
                </Button>
                <Menu
                  anchorEl={teamsAnchor}
                  open={Boolean(teamsAnchor)}
                  onClose={handleTeamsClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  transitionDuration={0}
                >
                  {teamNavItems.map((item) => (
                    <MenuItem
                      key={item.path}
                      component={Link}
                      to={item.path}
                      selected={isActive(item.path)}
                      onClick={handleTeamsClose}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Menu>

                {/* Questions dropdown */}
                <Button
                  variant={isGroupActive(questionNavItems) ? 'contained' : 'outlined'}
                  color={isGroupActive(questionNavItems) ? 'primary' : 'inherit'}
                  sx={isGroupActive(questionNavItems) ? { bgcolor: 'action.selected' } : {}}
                  endIcon={<KeyboardArrowDownIcon />}
                  onClick={handleQuestionsOpen}
                >
                  Questions
                </Button>
                <Menu
                  anchorEl={questionsAnchor}
                  open={Boolean(questionsAnchor)}
                  onClose={handleQuestionsClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  transitionDuration={0}
                >
                  {questionNavItems.map((item) => (
                    <MenuItem
                      key={item.path}
                      component={Link}
                      to={item.path}
                      selected={isActive(item.path)}
                      onClick={handleQuestionsClose}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Menu>

                {/* More dropdown (Profile, About) */}
                <Button
                  variant={isActive('/profile') || isActive('/about') ? 'contained' : 'outlined'}
                  color={isActive('/profile') || isActive('/about') ? 'primary' : 'inherit'}
                  sx={isActive('/profile') || isActive('/about') ? { bgcolor: 'action.selected' } : {}}
                  endIcon={<KeyboardArrowDownIcon />}
                  onClick={handleMoreOpen}
                >
                  More
                </Button>
                <Menu
                  anchorEl={moreAnchor}
                  open={Boolean(moreAnchor)}
                  onClose={handleMoreClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  transitionDuration={0}
                >
                  <MenuItem
                    component={Link}
                    to="/profile"
                    selected={isActive('/profile')}
                    onClick={handleMoreClose}
                  >
                    <Badge
                      variant="dot"
                      invisible={!hasAchievements}
                      sx={{ ...ACHIEVEMENT_DOT_SX, pr: hasAchievements ? 1.5 : 0 }}
                    >
                      Profile
                    </Badge>
                  </MenuItem>
                  <MenuItem
                    component={Link}
                    to="/about"
                    selected={isActive('/about')}
                    onClick={handleMoreClose}
                  >
                    About
                  </MenuItem>
                </Menu>
              </Stack>

              {/* User Section */}
              <Box ml={4}>
                {user ? (
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography color="text.primary">
                      Welcome, <strong>{user.username}</strong>
                    </Typography>
                    <Button variant="contained" color="error" onClick={onLogout}>
                      Logout
                    </Button>
                  </Stack>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onShowLogin}
                    sx={{ ml: 2 }}
                  >
                    Login / Register
                  </Button>
                )}
              </Box>
            </>
          )}

          {/* Mobile Hamburger Menu */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open menu"
              edge="end"
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>

      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 250,
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
}